import {
  PropsWithChildren,
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn } from '@/utils';

export interface BaseButtonProps {
  variant: 'primary' | 'secondary' | 'text' | 'danger';
  className?: string;
  disabled?: boolean;
}

interface ButtonType
  extends BaseButtonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  type: 'button' | 'submit';
  onClick?: () => void;
}

interface LinkType
  extends BaseButtonProps,
    AnchorHTMLAttributes<HTMLAnchorElement> {
  type: 'link';
  href: string;
  target?: '_blank' | '_self' | '_parent' | '_top' | string;
}

type Props = ButtonType | LinkType;

const baseClass =
  'relative rounded-[8px] text-sm px-[18px] py-[10px] font-bold ease duration-200 border disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gdt-blue-light';
const primaryClass = (isMobile: boolean) =>
  `${baseClass} bg-gdt-blue font-semibold text-white border-gdt-blue ${
    !isMobile ? 'hover:shadow-btn' : ''
  }`;
const secondaryClass = (isMobile: boolean) =>
  `${baseClass} font-bold bg-gdt-blue-xl2 text-gdt-blue border-gdt-blue-xl2 ${
    !isMobile ? 'hover:bg-[#CCD9FF] hover:border-gdt-blue' : ''
  }`;

const dangerClass = (isMobile: boolean) =>
  `${baseClass} font-semibold bg-gdt-status-red-light text-gdt-status-red border-gdt-status-red-light  focus-visible:ring-gdt-status-red-light ${
    !isMobile
      ? 'hover:border-gdt-status-red hover:bg-opacity-30 hover:bg-gdt-status-red'
      : ''
  }`;
const textClass = (isMobile: boolean) =>
  `bg-transparent underline text-gdt-blue ${
    !isMobile ? 'hover:text-gdt-blue-dark' : ''
  } text-sm font-normal ease duration-200`;
const disabledClass = 'opacity-50 cursor-not-allowed';

const Button = (props: PropsWithChildren<Props>) => {
  const { isMobile } = useIsDesktop();
  const { variant, type, children, disabled } = props;

  const classes = cn({
    [`${primaryClass(isMobile)}`]: variant === 'primary',
    [`${secondaryClass(isMobile)}`]: variant === 'secondary',
    [`${dangerClass(isMobile)}`]: variant === 'danger',
    [`${textClass(isMobile)}`]: variant === 'text',
    [`${disabledClass}`]: disabled,
    'inline-block': type === 'link',
  });

  if (type === 'link') {
    const { href, target, className, onClick } = props;
    return (
      <Link
        className={cn(classes, className)}
        rel={target === '_blank' ? 'nofollow noopener noreferrer' : undefined}
        target={target}
        to={href}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  if (type === 'submit' || type === 'button' || type === 'buttonIcon') {
    const { onClick, className, ...rest } = props;

    return (
      <button
        className={cn(classes, className)}
        onClick={onClick}
        {...rest}
        disabled={disabled}
      >
        {children}
      </button>
    );
  }

  return null;
};

export default Button;
